import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Grid,
  Group,
  Image,
  List,
  Rating,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconDownload,
  IconPuzzle,
  IconSearch,
  IconSettings,
  IconStar,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AutoDataTable } from "../../components/AutoDataTable";

// ── GraphQL Queries & Mutations ──

const MARKETPLACE_APPS_QUERY = gql`
  query marketplaceApps(
    $cursor: String
    $count: Float
    $filter: MarketplaceAppsFilterInput
  ) {
    marketplaceApps(page: { after: $cursor, first: $count }, filter: $filter) {
      nodes {
        id
        name
        shortDescription
        icon
        categories
        version
        manifest {
          permissions
          frontendModules {
            name
            displayName
            description
          }
        }
        stats {
          totalInstallations
          averageRating
          totalReviews
        }
        installation {
          id
          status
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const MARKETPLACE_APP_QUERY = gql`
  query marketplaceApp($id: ID!) {
    marketplaceApp(id: $id) {
      id
      name
      description
      shortDescription
      icon
      screenshots
      categories
      version
      privacyPolicyUrl
      supportUrl
      websiteUrl
      manifest {
        permissions
        frontendModules {
          name
          displayName
          description
        }
      }
      createdAt
      stats {
        totalInstallations
        averageRating
        totalReviews
      }
      installation {
        id
        status
        grantedPermissions
      }
      reviews(page: { first: 10 }) {
        nodes {
          id
          rating
          title
          body
          createdAt
        }
        totalCount
      }
    }
  }
`;

const MY_INSTALLATIONS_QUERY = gql`
  query myMarketplaceInstallations($cursor: String, $count: Float) {
    myMarketplaceInstallations(page: { after: $cursor, first: $count }) {
      nodes {
        id
        marketplaceAppId
        status
        grantedPermissions
        createdAt
        marketplaceApp {
          id
          name
          shortDescription
          icon
          version
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const INSTALL_APP_MUTATION = gql`
  mutation installMarketplaceApp($input: InstallMarketplaceAppInput!) {
    installMarketplaceApp(input: $input) {
      id
      status
      grantedPermissions
    }
  }
`;

const UNINSTALL_APP_MUTATION = gql`
  mutation uninstallMarketplaceApp($marketplaceAppId: ID!) {
    uninstallMarketplaceApp(marketplaceAppId: $marketplaceAppId)
  }
`;

const SUBMIT_REVIEW_MUTATION = gql`
  mutation submitMarketplaceAppReview(
    $input: SubmitMarketplaceAppReviewInput!
  ) {
    submitMarketplaceAppReview(input: $input) {
      id
      rating
      title
      body
    }
  }
`;

// ── App Card Component ──

interface FrontendModuleData {
  name: string;
  displayName: string;
  description?: string;
  version: string;
  propsSchema?: Record<
    string,
    { type: string; label: string; description?: string; required?: boolean }
  >;
  events?: Array<{ name: string; description?: string }>;
}

interface AppManifestData {
  permissions?: string[];
  frontendModules?: FrontendModuleData[];
}

interface AppCardData {
  id: string;
  name: string;
  shortDescription: string;
  icon?: string;
  categories: string[];
  version: string;
  manifest?: AppManifestData | null;
  stats?: {
    totalInstallations: number;
    averageRating: number;
    totalReviews: number;
  };
  installation?: {
    id: string;
    status: string;
  } | null;
}

function AppCard({ app, onClick }: { app: AppCardData; onClick: () => void }) {
  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{ cursor: "pointer" }}
      onClick={onClick}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="sm">
          {app.icon && (
            <Image
              src={app.icon}
              width={40}
              height={40}
              radius="md"
              alt={app.name}
            />
          )}
          <div>
            <Text fw={600} size="md">
              {app.name}
            </Text>
            <Text size="xs" c="dimmed">
              v{app.version}
            </Text>
          </div>
        </Group>
        <Group gap={4}>
          {(app.manifest?.frontendModules?.length ?? 0) > 0 && (
            <Tooltip label="Has frontend modules">
              <Badge
                color="violet"
                variant="light"
                leftSection={<IconPuzzle size={12} />}
              >
                Modules
              </Badge>
            </Tooltip>
          )}
          {app.installation?.status === "ACTIVE" && (
            <Badge color="green" variant="light">
              Installed
            </Badge>
          )}
        </Group>
      </Group>

      <Text size="sm" c="dimmed" lineClamp={2} mb="sm">
        {app.shortDescription}
      </Text>

      <Group justify="space-between">
        <Group gap="xs">
          {app.categories.slice(0, 2).map((cat) => (
            <Badge key={cat} variant="outline" size="xs">
              {cat}
            </Badge>
          ))}
        </Group>
        {app.stats && (
          <Group gap={4}>
            <IconStar size={14} />
            <Text size="xs">
              {app.stats.averageRating.toFixed(1)} ({app.stats.totalReviews})
            </Text>
          </Group>
        )}
      </Group>
    </Card>
  );
}

// ── Browse Page ──

type BrowseTab = "all" | "modules";

export function MarketplaceBrowse() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState<BrowseTab>("all");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading } = useQuery<any>(MARKETPLACE_APPS_QUERY, {
    variables: {
      count: 50,
      filter: {
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(activeTab === "modules" ? { hasModules: true } : {}),
      },
    },
    fetchPolicy: "network-only",
  });

  const allApps: AppCardData[] = data?.marketplaceApps?.nodes ?? [];

  // Client-side fallback filter for "modules" tab in case backend doesn't support hasModules yet
  const apps =
    activeTab === "modules"
      ? allApps.filter(
          (app) => (app.manifest?.frontendModules?.length ?? 0) > 0,
        )
      : allApps;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>Marketplace</Title>
        <TextInput
          placeholder="Search apps..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ width: 300 }}
        />
      </Group>

      <Tabs
        value={activeTab}
        onChange={(value) => {
          if (value === "all" || value === "modules") {
            setActiveTab(value);
          } else {
            setActiveTab("all");
          }
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="all">All Apps</Tabs.Tab>
          <Tabs.Tab value="modules" leftSection={<IconPuzzle size={14} />}>
            With Modules
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {loading && <Text c="dimmed">Loading apps...</Text>}

      <SimpleGrid cols={{ base: 1, sm: 3, md: 3 }} spacing="lg">
        {apps.map((app) => (
          <AppCard
            key={app.id}
            app={app}
            onClick={() => navigate(`/marketplace/browse/${app.id}`)}
          />
        ))}
      </SimpleGrid>

      {!loading && apps.length === 0 && (
        <Text c="dimmed" ta="center" py="xl">
          {activeTab === "modules"
            ? "No apps with frontend modules found."
            : "No apps found. Check back later for new extensions."}
        </Text>
      )}
    </Stack>
  );
}

// ── App Detail Page ──

interface ConsentModalBodyProps {
  appName: string;
  permissions: string[];
  installing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConsentModalBody({
  appName,
  permissions,
  installing,
  onCancel,
  onConfirm,
}: ConsentModalBodyProps) {
  const [consentAccepted, setConsentAccepted] = useState(false);

  return (
    <Stack gap="md">
      <Text size="sm">
        <strong>{appName}</strong> is requesting access to the following
        permissions. By installing, you grant this app access to:
      </Text>
      {permissions.length > 0 ? (
        <List spacing="xs" size="sm">
          {permissions.map((scope) => (
            <List.Item key={scope}>
              <Badge variant="light" size="sm">
                {scope}
              </Badge>
            </List.Item>
          ))}
        </List>
      ) : (
        <Text size="sm" c="dimmed">
          This app does not request any permissions.
        </Text>
      )}
      <Divider />
      <Checkbox
        label="I understand and agree to grant these permissions"
        checked={consentAccepted}
        onChange={(e) => setConsentAccepted(e.currentTarget.checked)}
      />
      <Group justify="right">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!consentAccepted}
          loading={installing}
          onClick={onConfirm}
        >
          Install
        </Button>
      </Group>
    </Stack>
  );
}

interface ReviewModalBodyProps {
  onSubmit: (values: {
    rating: number;
    title: string;
    body: string;
  }) => Promise<void>;
}

function ReviewModalBody({ onSubmit }: ReviewModalBodyProps) {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ rating, title, body });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      <div>
        <Text size="sm" mb={4}>
          Rating
        </Text>
        <Rating value={rating} onChange={setRating} />
      </div>
      <TextInput
        label="Title"
        placeholder="Summary of your review"
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
        maxLength={100}
      />
      <Textarea
        label="Review"
        placeholder="Share your experience with this app"
        value={body}
        onChange={(e) => setBody(e.currentTarget.value)}
        maxLength={2000}
        minRows={4}
      />
      <Button
        onClick={handleSubmit}
        loading={submitting}
        disabled={!title || !body}
      >
        Submit Review
      </Button>
    </Stack>
  );
}

export function MarketplaceAppDetail() {
  const navigate = useNavigate();
  const { appId } = useParams();

  const { data, loading, refetch } = useQuery<any>(MARKETPLACE_APP_QUERY, {
    variables: { id: appId },
    skip: !appId,
  });

  const [installApp, { loading: installing }] =
    useMutation<any>(INSTALL_APP_MUTATION);
  const [uninstallApp, { loading: uninstalling }] = useMutation<any>(
    UNINSTALL_APP_MUTATION,
  );
  const [submitReview] = useMutation<any>(SUBMIT_REVIEW_MUTATION);

  const app = data?.marketplaceApp;
  if (loading) return <Text>Loading...</Text>;
  if (!app) return <Text>App not found</Text>;

  const isInstalled = app.installation?.status === "ACTIVE";
  const manifest = app.manifest as AppManifestData | null;
  const frontendModules = manifest?.frontendModules ?? [];

  const performInstall = async (modalId: string) => {
    modals.close(modalId);
    try {
      await installApp({
        variables: {
          input: {
            marketplaceAppId: app.id,
            grantedPermissions: manifest?.permissions ?? [],
          },
        },
      });
      notifications.show({
        title: "Installed",
        message: `${app.name} has been installed successfully`,
        color: "green",
        icon: <IconCheck size={16} />,
      });
      refetch();
    } catch (error) {
      notifications.show({
        title: "Installation failed",
        message: (error as Error).message,
        color: "red",
      });
    }
  };

  const handleInstallClick = () => {
    const modalId = `install-consent-${app.id}`;
    modals.open({
      modalId,
      title: "Grant Permissions",
      children: (
        <ConsentModalBody
          appName={app.name}
          permissions={manifest?.permissions ?? []}
          installing={installing}
          onCancel={() => modals.close(modalId)}
          onConfirm={() => performInstall(modalId)}
        />
      ),
    });
  };

  const handleWriteReviewClick = () => {
    const modalId = `write-review-${app.id}`;
    modals.open({
      modalId,
      title: "Write a Review",
      children: (
        <ReviewModalBody
          onSubmit={async ({ rating, title, body }) => {
            try {
              await submitReview({
                variables: {
                  input: {
                    marketplaceAppId: app.id,
                    rating,
                    title,
                    body,
                  },
                },
              });
              notifications.show({
                title: "Review submitted",
                message: "Thanks for your feedback!",
                color: "green",
              });
              modals.close(modalId);
              refetch();
            } catch (error) {
              notifications.show({
                title: "Failed to submit review",
                message: (error as Error).message,
                color: "red",
              });
            }
          }}
        />
      ),
    });
  };

  const handleUninstall = async () => {
    try {
      await uninstallApp({
        variables: { marketplaceAppId: app.id },
      });
      notifications.show({
        title: "Uninstalled",
        message: `${app.name} has been removed`,
        color: "green",
      });
      refetch();
    } catch (error) {
      notifications.show({
        title: "Uninstall failed",
        message: (error as Error).message,
        color: "red",
      });
    }
  };

  const reviews = app.reviews?.nodes ?? [];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="md">
          {app.icon && (
            <Image
              src={app.icon}
              width={64}
              height={64}
              radius="md"
              alt={app.name}
            />
          )}
          <div>
            <Title order={3}>{app.name}</Title>
            <Text c="dimmed" size="sm">
              v{app.version}
            </Text>
          </div>
        </Group>

        <Group>
          {isInstalled ? (
            <Button
              color="red"
              variant="light"
              leftSection={<IconTrash size={16} />}
              loading={uninstalling}
              onClick={handleUninstall}
            >
              Uninstall
            </Button>
          ) : (
            <Button
              leftSection={<IconDownload size={16} />}
              loading={installing}
              onClick={handleInstallClick}
            >
              Install
            </Button>
          )}
          <Button variant="light" onClick={handleWriteReviewClick}>
            Write Review
          </Button>
        </Group>
      </Group>

      {/* Stats */}
      {app.stats && (
        <Group gap="xl">
          <div>
            <Text size="sm" c="dimmed">
              Installations
            </Text>
            <Text size="xl" fw={700}>
              {app.stats.totalInstallations}
            </Text>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Rating
            </Text>
            <Group gap={4}>
              <Rating value={app.stats.averageRating} readOnly fractions={2} />
              <Text size="sm">({app.stats.totalReviews})</Text>
            </Group>
          </div>
        </Group>
      )}

      {/* Description */}
      <Card withBorder>
        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
          {app.description}
        </Text>
      </Card>

      {/* Frontend Modules */}
      {frontendModules.length > 0 && (
        <div>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="violet">
                <IconPuzzle size={14} />
              </ThemeIcon>
              <Title order={5}>Frontend Modules</Title>
              <Badge size="xs" variant="light" color="violet">
                {frontendModules.length}
              </Badge>
            </Group>
            {isInstalled && (
              <Button
                variant="light"
                size="xs"
                leftSection={<IconSettings size={14} />}
                onClick={() => navigate("/platform/modules")}
              >
                Configure
              </Button>
            )}
          </Group>
          <Stack gap="sm">
            {frontendModules.map((mod) => {
              const propEntries = Object.entries(mod.propsSchema ?? {});
              const events = mod.events ?? [];

              return (
                <Card key={mod.name} withBorder padding="sm">
                  <Group justify="space-between" mb={4}>
                    <Group gap="xs">
                      <Text fw={600} size="sm">
                        {mod.displayName}
                      </Text>
                      <Badge size="xs" variant="outline" color="gray">
                        v{mod.version}
                      </Badge>
                    </Group>
                  </Group>
                  {mod.description && (
                    <Text size="sm" c="dimmed" mb="xs">
                      {mod.description}
                    </Text>
                  )}
                  <Group gap="lg">
                    {propEntries.length > 0 && (
                      <div>
                        <Text size="xs" fw={600} mb={2}>
                          Props
                        </Text>
                        <Group gap={4}>
                          {propEntries.map(([key, prop]) => (
                            <Tooltip
                              key={key}
                              label={
                                prop.description ??
                                `${prop.type}${prop.required ? " (required)" : ""}`
                              }
                            >
                              <Badge size="xs" variant="outline">
                                {prop.label}
                              </Badge>
                            </Tooltip>
                          ))}
                        </Group>
                      </div>
                    )}
                    {events.length > 0 && (
                      <div>
                        <Text size="xs" fw={600} mb={2}>
                          Events
                        </Text>
                        <Group gap={4}>
                          {events.map((evt) => (
                            <Tooltip
                              key={evt.name}
                              label={evt.description ?? evt.name}
                            >
                              <Badge size="xs" variant="outline" color="orange">
                                {evt.name}
                              </Badge>
                            </Tooltip>
                          ))}
                        </Group>
                      </div>
                    )}
                  </Group>
                </Card>
              );
            })}
          </Stack>
        </div>
      )}

      {/* Screenshots */}
      {app.screenshots?.length > 0 && (
        <div>
          <Title order={5} mb="sm">
            Screenshots
          </Title>
          <Grid>
            {app.screenshots.map((url: string, idx: number) => (
              <Grid.Col key={idx} span={4}>
                <Image src={url} radius="md" alt={`Screenshot ${idx + 1}`} />
              </Grid.Col>
            ))}
          </Grid>
        </div>
      )}

      {/* Permissions */}
      {manifest?.permissions && manifest.permissions.length > 0 && (
        <div>
          <Title order={5} mb="sm">
            Required Permissions
          </Title>
          <Group gap="xs">
            {manifest.permissions.map((scope: string) => (
              <Badge key={scope} variant="outline">
                {scope}
              </Badge>
            ))}
          </Group>
        </div>
      )}

      {/* Links */}
      <Group gap="lg">
        {app.privacyPolicyUrl && (
          <Text
            component="a"
            href={app.privacyPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            color="blue"
          >
            Privacy Policy
          </Text>
        )}
        {app.supportUrl && (
          <Text
            component="a"
            href={app.supportUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            color="blue"
          >
            Support
          </Text>
        )}
        {app.websiteUrl && (
          <Text
            component="a"
            href={app.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            color="blue"
          >
            Website
          </Text>
        )}
      </Group>

      {/* Reviews */}
      <div>
        <Title order={5} mb="sm">
          Reviews ({app.reviews?.totalCount ?? 0})
        </Title>
        <Stack gap="sm">
          {reviews.map(
            (review: {
              id: string;
              rating: number;
              title: string;
              body: string;
              createdAt: string;
            }) => (
              <Card key={review.id} withBorder padding="sm">
                <Group justify="space-between" mb={4}>
                  <Text fw={600} size="sm">
                    {review.title}
                  </Text>
                  <Rating value={review.rating} readOnly size="xs" />
                </Group>
                <Text size="sm" c="dimmed">
                  {review.body}
                </Text>
                <Text size="xs" c="dimmed" mt={4}>
                  {new Date(review.createdAt).toLocaleDateString()}
                </Text>
              </Card>
            ),
          )}
          {reviews.length === 0 && (
            <Text size="sm" c="dimmed">
              No reviews yet. Be the first to review this app.
            </Text>
          )}
        </Stack>
      </div>

    </Stack>
  );
}

// ── My Installed Apps ──

interface InstalledAppNode {
  id: string;
  marketplaceAppId: string;
  status: string;
  grantedPermissions: string[];
  createdAt: string;
  marketplaceApp: {
    id: string;
    name: string;
    shortDescription: string;
    icon?: string;
    version: string;
  };
}

export function MarketplaceMyApps() {
  return (
    <AutoDataTable
      query={MY_INSTALLATIONS_QUERY}
      createButtonText={false}
      disableNavigation
      columns={[
        {
          accessor: "marketplaceApp.name",
          title: "App",
          rawElement: (item: InstalledAppNode) => (
            <Group gap="sm">
              {item.marketplaceApp?.icon && (
                <Image
                  src={item.marketplaceApp.icon}
                  width={24}
                  height={24}
                  radius="sm"
                  alt={item.marketplaceApp?.name}
                />
              )}
              <Text size="sm">{item.marketplaceApp?.name}</Text>
            </Group>
          ),
        },
        {
          accessor: "marketplaceApp.version",
          title: "Version",
          rawElement: (item: InstalledAppNode) => (
            <Text size="sm">v{item.marketplaceApp?.version}</Text>
          ),
        },
        {
          accessor: "grantedPermissions",
          title: "Permissions",
          rawElement: (item: InstalledAppNode) => (
            <Group gap={4}>
              {(item.grantedPermissions ?? [])
                .slice(0, 3)
                .map((scope: string) => (
                  <Badge key={scope} size="xs" variant="outline">
                    {scope}
                  </Badge>
                ))}
              {(item.grantedPermissions ?? []).length > 3 && (
                <Text size="xs" c="dimmed">
                  +{item.grantedPermissions.length - 3} more
                </Text>
              )}
            </Group>
          ),
        },
        {
          accessor: "createdAt",
          title: "Installed",
          rawElement: (item: InstalledAppNode) => (
            <Text size="sm">
              {new Date(item.createdAt).toLocaleDateString()}
            </Text>
          ),
        },
      ]}
    />
  );
}

// ── Home Page ──

export function MarketplaceHome() {
  return (
    <Stack gap="lg">
      <Title order={3}>Marketplace</Title>
      <Text c="dimmed">
        Discover and install apps that extend your WellPlayed platform. Browse
        the marketplace to find tournament tools, player engagement features,
        analytics integrations, and more.
      </Text>
      <MarketplaceBrowse />
    </Stack>
  );
}
